import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A clean fullscreen image gallery for product media zoom.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} [counterCurrent]
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();

  /** @type {number} */
  #activeIndex = 0;

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  /** @type {{ html: string, body: string } | null} */
  #previousOverflow = null;

  /** @type {{ x: number, y: number } | null} */
  #touchStart = null;

  /**
   * Opens the zoom dialog on the selected image index.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  open(index, event) {
    event.preventDefault();

    const { dialog } = this.refs;
    const mediaIndex = this.#normalizeIndex(index);

    this.#triggerElement =
      event.target instanceof HTMLElement
        ? event.target.closest('button, slideshow-slide, li, .product-media-container')
        : null;
    this.#lockPageScroll();
    this.selectMedia(mediaIndex, { syncGallery: false });

    if (!dialog.open) dialog.showModal();

    requestAnimationFrame(() => {
      const closeButton = dialog.querySelector('.dialog-zoomed-gallery__close-button');
      if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
    });
  }

  /**
   * Preloads the high-resolution image for a media item when available.
   *
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.sizes = image.sizes;
    newImage.srcset = image.srcset;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();

    this.#unlockPageScroll();
    window.dispatchEvent(new DialogCloseEvent());

    const trigger = this.#triggerElement;
    this.#triggerElement = null;

    if (trigger?.isConnected) {
      requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goPrevious();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goNext();
    }
  }

  /**
   * Closes when the customer clicks outside the displayed image or controls.
   *
   * @param {PointerEvent} event
   */
  handleBackdropClick(event) {
    const path = event.composedPath();

    const clickedControl = path.some(
      (node) =>
        node instanceof Element &&
        (node.classList.contains('dialog-zoomed-gallery__close-button') ||
          node.classList.contains('dialog-zoomed-gallery__nav'))
    );
    if (clickedControl) return;

    const clickedImage = path.some(
      (node) => node instanceof Element && node.classList.contains('product-media__image')
    );
    if (!clickedImage) this.close();
  }

  goPrevious() {
    this.selectMedia(this.#activeIndex - 1);
  }

  goNext() {
    this.selectMedia(this.#activeIndex + 1);
  }

  /**
   * @param {TouchEvent} event
   */
  handleTouchStart(event) {
    const touch = event.changedTouches[0];
    if (!touch) return;

    this.#touchStart = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * @param {TouchEvent} event
   */
  handleTouchEnd(event) {
    if (!this.#touchStart) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#touchStart.x;
    const deltaY = touch.clientY - this.#touchStart.y;
    this.#touchStart = null;

    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) {
      this.goNext();
    } else {
      this.goPrevious();
    }
  }

  /**
   * Selects a gallery image and announces the original product media index.
   *
   * @param {number} index
   * @param {{ syncGallery?: boolean }} [options]
   */
  selectMedia(index, options = { syncGallery: true }) {
    const { media } = this.refs;
    if (!media.length) return;

    const normalizedIndex = this.#normalizeIndex(index);
    this.#activeIndex = normalizedIndex;

    media.forEach((item, itemIndex) => {
      item.setAttribute('aria-hidden', `${itemIndex !== normalizedIndex}`);
    });

    this.refs.counterCurrent?.replaceChildren(document.createTextNode(`${normalizedIndex + 1}`));

    const activeMedia = media[normalizedIndex];
    if (activeMedia) this.loadHighResolutionImage(activeMedia);

    if (options.syncGallery !== false) {
      const originalMediaIndex = Number(activeMedia?.dataset.galleryMediaIndex ?? normalizedIndex);
      this.dispatchEvent(new ZoomMediaSelectedEvent(originalMediaIndex));
    }
  }

  /**
   * @param {number} index
   */
  #normalizeIndex(index) {
    const count = this.refs.media.length;
    if (!count || Number.isNaN(index)) return 0;

    return ((index % count) + count) % count;
  }

  #lockPageScroll() {
    if (this.#previousOverflow) return;

    this.#previousOverflow = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
    };

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  #unlockPageScroll() {
    if (!this.#previousOverflow) return;

    document.documentElement.style.overflow = this.#previousOverflow.html;
    document.body.style.overflow = this.#previousOverflow.body;
    this.#previousOverflow = null;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
